import { IPaymentContract } from "@features/payment/ui";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IRouterContract } from "@features/shared/router";
import { IUserAuthContract } from "@features/student/auth/model";
import { createEvent, createStore, sample } from "effector";
import { checkUpsellPurchased, UpsellStatus } from "@features/helpers";
import { routePathNameChanged } from "@features/app/model";
import { interval, reset } from "patronum";
import { IUpsellContract } from "../model";
import { ISubscriptionContract } from "@features/subscription/model";
import { IAppRouteUnitsContract } from "@features/app/route-units";
import { IUpsellUIContract } from ".";

const TARGET_IDS = new Set(["30k_prompts", "30k_prompts_sale"]);

const buyButtonClicked = createEvent();
const buyButtonDisabled = createEvent();
const buyButtonReset = createEvent();
const modalOpened = createEvent();
const modalClosed = createEvent();
const pollingStarted = createEvent();
const pollingStopped = createEvent();

const $isModalOpen = createStore(false)
    .on(modalOpened, () => true)
    .on(modalClosed, () => false);
const $upsellIds = createStore<string[]>([]);
const $ultimateAiPackStatus = createStore<UpsellStatus | null>(null);
const checkRequested = createEvent();
const checkFinished = createEvent();
const $isCheckRequested = createStore(false).on(checkRequested, () => true);
const $isCheckPending = createStore(false)
    .on(checkRequested, () => true)
    .on(checkFinished, () => false);
const $isBuyButtonDisabled = createStore(true)
    .on(buyButtonClicked, () => true)
    .on(buyButtonDisabled, () => true)
    .on(buyButtonReset, () => false);

const { tick } = interval({
    timeout: 10000,
    start: pollingStarted,
    stop: pollingStopped,
    leading: false,
    trailing: false
});

const contract = new ModelContract(
    { $isModalOpen, $ultimateAiPackStatus, $upsellIds, $isBuyButtonDisabled },
    { buyButtonClicked, modalOpened, modalClosed },
    {},
    {}
);

export type IUltimateAiPackContract = typeof contract;

export class UltimateAiPackModule extends ModuleDescription<IUltimateAiPackContract> {
    constructor(
        public authModule: IUserAuthContract,
        public routerModule: IRouterContract,
        public paymentModule: IPaymentContract,
        public upsellModule: IUpsellContract,
        public upsellUiModule: IUpsellUIContract,
        public subscriptionsModule: ISubscriptionContract,
        public routesModule: IAppRouteUnitsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        // The chain holds upsell ids for every provider (truegate plans are
        // resolved on the server now), so the check needs no catalog.
        sample({
            source: {
                upsells: this.upsellModule.stores.$upsells,
                subscriptions: this.subscriptionsModule.stores.$subscriptions,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem
            },
            filter: (s) => s.upsells.length > 0,
            fn: (s) => {
                const upsellIds = s.upsells.filter((u) => TARGET_IDS.has(u.upsellId)).map((u) => u.upsellId);
                if (upsellIds.length === 0) return upsellIds;
                const result = [...upsellIds];
                if (!result.includes("bundle")) {
                    result.push("bundle");
                }
                // The Complete Bundle grants this pack too.
                if (!result.includes("complete_bundle")) {
                    result.push("complete_bundle");
                }
                return result;
            },
            target: $upsellIds
        });

        sample({
            clock: modalClosed,
            fn: () => "/upsell/30k_prompts_sale",
            target: this.routerModule.events.routerReplace
        });

        sample({
            clock: this.upsellUiModule.events.upsellSaleModalOpened,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId === "30k_prompts",
            target: modalOpened
        });

        sample({
            clock: buyButtonClicked,
            target: this.upsellModule.events.upsellBought
        });

        sample({
            source: {
                upsellIds: $upsellIds,
                upsellPath: this.routesModule.stores.upsell.$path,
                isHomeRoute: this.routesModule.stores.home.$isActive,
                isProfileRoute: this.routesModule.stores.profile.$isActive,
                isLibraryRoute: this.routesModule.stores.library.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive,
                isChallengesRoute: this.routesModule.stores.challenges.$isActive,
                promptLibraryQuery: this.routesModule.stores.promptsLibrary.$query,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                isCheckRequested: $isCheckRequested,
                isCheckPending: $isCheckPending
            },
            filter: (s) =>
                s.upsellIds.length > 0 &&
                (s.isHomeRoute ||
                    s.isProfileRoute ||
                    // Desktop right rail shows the packs on these routes too.
                    s.isLibraryRoute ||
                    s.isAiToolsRoute ||
                    s.isChallengesRoute ||
                    s.promptLibraryQuery.pack === "ultimate_ai" ||
                    (s.upsellPath !== null &&
                        (s.upsellPath.includes("30k_prompts") || s.upsellPath.includes("bundle")))) &&
                ["solidgate", "ecommpay", "apple", "truegate"].includes(s.paymentSystem) &&
                !s.isCheckRequested &&
                !s.isCheckPending,
            fn: (s) => s.upsellIds,
            target: [this.upsellModule.effects.checkUpsellPurchasedFx, checkRequested]
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.done,
            source: {
                upsellIds: $upsellIds,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                awaitingVerdict: this.upsellModule.stores.$upsellIdsAwaitingVerdict
            },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)) &&
                ["solidgate", "ecommpay", "apple", "truegate"].includes(s.paymentSystem),
            fn: (s, { result }) => {
                const statuses = s.upsellIds.map((upsellId) => checkUpsellPurchased(result, upsellId));

                if (statuses.includes("Success")) return "Success";
                if (statuses.includes("Processing")) return "Processing";
                // A just-bought pack in 3ds_verify reads as "Fail" until its bank
                // dialog is done; it is in progress — keep polling until the
                // webhook confirms instead of showing it as unpurchased.
                if (s.upsellIds.some((upsellId) => s.awaitingVerdict.includes(upsellId))) return "Processing";

                return "Fail";
            },
            target: $ultimateAiPackStatus
        });
        sample({
            source: {
                ultimateAiPackStatus: $ultimateAiPackStatus,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                upsellPath: this.routesModule.stores.upsell.$path,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag
            },
            filter: (s) =>
                (s.ultimateAiPackStatus === "Success" || s.ultimateAiPackStatus === "Processing") &&
                s.isUpsellRoute &&
                s.upsellPath !== null &&
                s.upsellPath.includes("30k_prompts") &&
                !s.upsellPath.includes("30k_prompts_complete_pack") &&
                !s.firstLoginFlag,
            target: [this.upsellUiModule.events.packAccessRedirectTriggered, buyButtonDisabled]
        });

        sample({
            source: {
                ultimateAiPackStatus: $ultimateAiPackStatus,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                upsellPath: this.routesModule.stores.upsell.$path
            },
            filter: (s) =>
                s.ultimateAiPackStatus === "Fail" &&
                s.isUpsellRoute &&
                s.upsellPath !== null &&
                s.upsellPath.includes("30k_prompts") &&
                !s.upsellPath.includes("30k_prompts_complete_pack"),
            target: buyButtonReset
        });

        sample({
            clock: $ultimateAiPackStatus,
            source: {
                isUpsellRoute: this.routesModule.stores.upsell.$isActive
            },
            filter: (s, p) => !s.isUpsellRoute && p === "Processing",
            target: pollingStarted
        });

        sample({
            clock: $ultimateAiPackStatus,
            filter: (status) => status === "Success" || status === "Fail",
            target: pollingStopped
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.finally,
            source: { upsellIds: $upsellIds },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)),
            fn: () => undefined,
            target: checkFinished
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.fail,
            source: { upsellIds: $upsellIds },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)),
            fn: () => undefined,
            target: [pollingStarted, buyButtonReset]
        });

        sample({
            clock: tick,
            source: { upsellIds: $upsellIds, isCheckPending: $isCheckPending },
            filter: (s) => s.upsellIds.length > 0 && !s.isCheckPending,
            fn: (s) => s.upsellIds,
            target: [this.upsellModule.effects.checkUpsellPurchasedFx, checkRequested]
        });

        reset({
            clock: routePathNameChanged,
            target: [$isModalOpen, $ultimateAiPackStatus, $isCheckRequested]
        });
    }
}
